import type { ActorDto } from '@newo/api-client';
import {
	NavigationIntent,
	OrganizationType,
	routingClient
} from '@newo/routing-client';
import {
	createContext,
	type FC,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { useFormContext } from 'react-hook-form';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import {
	STEPPER_PORTAL_HANDOFF_DELAY_MS,
	STEPPER_STEP_TRANSITION_PROGRESS_TIMEOUT_MS
} from '@/stepper/constants';
import useStepperQueryParams from '@/stepper/hooks/useStepperQueryParams';
import useStepperTraining from '@/stepper/hooks/useStepperTraining';
import { resetStepperRuntime } from '@/stepper/resetStepperRuntime';
import {
	type StepperOnboardingFormValues,
	StepperSetupStepKey
} from '@/stepper/types';
import { getNextStepperSetupStepKey } from '@/stepper/utils';
import useAppStore from '@/store/app';
import { AgentInfoState } from '@/types/agent-realtime';

type StepperAgentProfileHydration = {
	agentName?: string;
	agentPhoneNumber?: string;
	businessName?: string;
	createdCustomerIdn?: string;
};

type StepperOnboardingContextValue = {
	activeStepKey: StepperSetupStepKey;
	agentPendingProgressRatio: number;
	closeTestCallModal: () => void;
	finishOnboarding: () => void;
	goToNextStep: () => void;
	goToNextStepFromWebsite: () => Promise<void>;
	goToStep: (stepKey: StepperSetupStepKey) => void;
	isAgentProgressActive: boolean;
	isAgentReady: boolean;
	isCustomerCreatePending: boolean;
	isLocked: boolean;
	isOnboardingStarted: boolean;
	isSetupActivationVisible: boolean;
	isSetupComplete: boolean;
	isTestCallModalVisible: boolean;
	isTestCallWaitingVisible: boolean;
	isTrainingPending: boolean;
	openTestCallModal: () => void;
	pendingAgentPhoneNumber: string;
	stepTransitionKey: StepperSetupStepKey | null;
	submitOnboarding: () => Promise<void>;
	updateAgentPendingProgress: (progressRatio: number) => void;
};

const StepperOnboardingContext =
	createContext<StepperOnboardingContextValue | null>(null);

type StepperOnboardingProviderProps = PropsWithChildren<{
	actor?: ActorDto;
}>;

export const StepperOnboardingProvider: FC<StepperOnboardingProviderProps> = ({
	actor,
	children
}) => {
	const [activeStepKey, setActiveStepKey] = useState(
		StepperSetupStepKey.ExtractWebsiteContent
	);
	const [stepTransitionKey, setStepTransitionKey] =
		useState<StepperSetupStepKey | null>(null);
	const [isOnboardingStarted, setIsOnboardingStarted] = useState(false);
	const [isAgentReady, setIsAgentReady] = useState(false);
	const [isLocked, setIsLocked] = useState(false);
	const [agentPendingProgressRatio, setAgentPendingProgressRatio] = useState(0);
	const [isTestCallModalVisible, setIsTestCallModalVisible] = useState(false);
	const [isTestCallWaitingVisible, setIsTestCallWaitingVisible] =
		useState(true);
	const [isTestCallFinished, setIsTestCallFinished] = useState(false);
	const [pendingAgentPhoneNumber, setPendingAgentPhoneNumber] = useState('');
	const stepTransitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null
	);
	const hasPortalHandoffStartedRef = useRef(false);
	const portalHandoffTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null
	);
	const hasAutoSubmittedWebsiteRef = useRef(false);
	const { getValues, setValue, trigger } =
		useFormContext<StepperOnboardingFormValues>();
	const { source: querySource } = useStepperQueryParams();
	const setCustomerIdn = useAppStore((state) => state.setCustomerIdn);
	const { isPending, submitOnboarding: sendOnboarding } =
		useStepperTraining(actor);

	const clearStepTransition = useCallback(() => {
		if (stepTransitionTimeoutRef.current) {
			globalThis.clearTimeout(stepTransitionTimeoutRef.current);
			stepTransitionTimeoutRef.current = null;
		}

		setStepTransitionKey(null);
	}, []);

	useEffect(
		() => () => {
			if (stepTransitionTimeoutRef.current) {
				globalThis.clearTimeout(stepTransitionTimeoutRef.current);
			}
		},
		[]
	);

	const goToStep = useCallback(
		(stepKey: StepperSetupStepKey) => {
			if (stepTransitionKey) {
				return;
			}

			setActiveStepKey(stepKey);
		},
		[stepTransitionKey]
	);

	const goToNextStep = useCallback(() => {
		clearStepTransition();
		setStepTransitionKey(activeStepKey);
		stepTransitionTimeoutRef.current = globalThis.setTimeout(() => {
			clearStepTransition();
			setActiveStepKey(getNextStepperSetupStepKey(activeStepKey));
		}, STEPPER_STEP_TRANSITION_PROGRESS_TIMEOUT_MS);
	}, [activeStepKey, clearStepTransition]);

	const goToNextStepFromWebsite = useCallback(async () => {
		const isWebsiteValid = await trigger('websiteUrl', { shouldFocus: true });

		if (!isWebsiteValid) {
			return;
		}

		goToNextStep();
	}, [goToNextStep, trigger]);

	const submitOnboarding = useCallback(async () => {
		const isContactValid = await trigger(
			['managerFullName', 'managerEmail', 'managerPhone'],
			{ shouldFocus: true }
		);

		if (!isContactValid) {
			return;
		}

		const isStarted = await sendOnboarding(getValues());

		if (!isStarted) {
			return;
		}

		setIsOnboardingStarted(true);
		setIsLocked(true);
		goToNextStep();
	}, [getValues, goToNextStep, sendOnboarding, trigger]);

	const openTestCallModal = useCallback(() => {
		setIsTestCallWaitingVisible(true);
		setIsTestCallModalVisible(true);
	}, []);

	const closeTestCallModal = useCallback(() => {
		setIsTestCallModalVisible(false);
	}, []);

	const applyAgentProfile = useCallback(
		({
			agentName,
			agentPhoneNumber,
			businessName,
			createdCustomerIdn
		}: StepperAgentProfileHydration) => {
			if (createdCustomerIdn) {
				setValue('createdCustomerIdn', createdCustomerIdn);
				setCustomerIdn(createdCustomerIdn);
			}

			if (agentName) {
				setValue('agentName', agentName);
			}

			if (agentPhoneNumber) {
				setValue('agentPhoneNumber', agentPhoneNumber);
			}

			if (businessName) {
				setValue('businessName', businessName);
			}
		},
		[setCustomerIdn, setValue]
	);

	useEffect(() => {
		const offOnboardingFinished = agentTrainingRealtime.onOnboardingFinished(
			(args) => {
				applyAgentProfile({
					agentName: args.agent_name,
					agentPhoneNumber: args.agent_phone_number || args.phone_number || '',
					businessName: args.business_name,
					createdCustomerIdn: args.created_customer_idn
				});
				setPendingAgentPhoneNumber(
					args.agent_phone_number || args.phone_number || ''
				);
				setIsAgentReady(true);
			}
		);

		const offAgentInfo = agentTrainingRealtime.onAgentInfo(
			({ body, state }) => {
				applyAgentProfile({
					agentName: body.agent_name,
					agentPhoneNumber: body.phone_number,
					businessName: body.business_name,
					createdCustomerIdn: body.created_customer_idn
				});

				if (state === AgentInfoState.Created) {
					setPendingAgentPhoneNumber(body.phone_number ?? '');
					setIsAgentReady(true);
				}
			}
		);

		const offFirstCustomerCallStarted =
			agentTrainingRealtime.onFirstCustomerCallStarted(() => {
				setIsTestCallWaitingVisible(false);
				setIsTestCallModalVisible(true);
			});

		const offFirstCustomerCallFinished =
			agentTrainingRealtime.onFirstCustomerCallFinished(() => {
				setIsTestCallModalVisible(false);
				setIsTestCallFinished(true);
			});

		return () => {
			offOnboardingFinished();
			offAgentInfo();
			offFirstCustomerCallStarted();
			offFirstCustomerCallFinished();
		};
	}, [applyAgentProfile]);

	useEffect(() => {
		if (hasAutoSubmittedWebsiteRef.current || !querySource) {
			return;
		}

		hasAutoSubmittedWebsiteRef.current = true;
		void goToNextStepFromWebsite();
	}, [goToNextStepFromWebsite, querySource]);

	const finishOnboarding = useCallback(() => {
		if (portalHandoffTimeoutRef.current) {
			globalThis.clearTimeout(portalHandoffTimeoutRef.current);
			portalHandoffTimeoutRef.current = null;
		}

		if (hasPortalHandoffStartedRef.current) {
			return;
		}

		const createdCustomerIdn =
			getValues('createdCustomerIdn') || useAppStore.getState().customerIdn;

		if (!createdCustomerIdn) {
			return;
		}

		hasPortalHandoffStartedRef.current = true;
		resetStepperRuntime();
		routingClient.emitNavigation({
			intent: NavigationIntent.CustomerAgentConfiguration,
			params: {
				organizationIdn: createdCustomerIdn,
				organizationType: OrganizationType.Customer
			}
		});
	}, [getValues]);

	useEffect(() => {
		if (!isTestCallFinished || hasPortalHandoffStartedRef.current) {
			return;
		}

		portalHandoffTimeoutRef.current = globalThis.setTimeout(
			finishOnboarding,
			STEPPER_PORTAL_HANDOFF_DELAY_MS
		);

		return () => {
			if (portalHandoffTimeoutRef.current) {
				globalThis.clearTimeout(portalHandoffTimeoutRef.current);
				portalHandoffTimeoutRef.current = null;
			}
		};
	}, [finishOnboarding, isTestCallFinished]);

	const value = useMemo<StepperOnboardingContextValue>(
		() => ({
			activeStepKey,
			agentPendingProgressRatio,
			closeTestCallModal,
			finishOnboarding,
			goToNextStep,
			goToNextStepFromWebsite,
			goToStep,
			isAgentProgressActive: isOnboardingStarted && !isAgentReady,
			isAgentReady,
			isCustomerCreatePending: isPending,
			isLocked,
			isOnboardingStarted,
			isSetupActivationVisible: isTestCallFinished,
			isSetupComplete: isTestCallFinished,
			isTestCallModalVisible,
			isTestCallWaitingVisible,
			isTrainingPending: isPending,
			openTestCallModal,
			pendingAgentPhoneNumber,
			stepTransitionKey,
			submitOnboarding,
			updateAgentPendingProgress: setAgentPendingProgressRatio
		}),
		[
			activeStepKey,
			agentPendingProgressRatio,
			closeTestCallModal,
			finishOnboarding,
			goToNextStep,
			goToNextStepFromWebsite,
			goToStep,
			isAgentReady,
			isLocked,
			isOnboardingStarted,
			isPending,
			isTestCallFinished,
			isTestCallModalVisible,
			isTestCallWaitingVisible,
			openTestCallModal,
			pendingAgentPhoneNumber,
			stepTransitionKey,
			submitOnboarding
		]
	);

	return (
		<StepperOnboardingContext.Provider value={value}>
			{children}
		</StepperOnboardingContext.Provider>
	);
};

export const useStepperOnboarding = (): StepperOnboardingContextValue => {
	const context = useContext(StepperOnboardingContext);

	if (!context) {
		throw new Error(
			'useStepperOnboarding must be used within StepperOnboardingProvider'
		);
	}

	return context;
};
